import type { CurrencyWithSubunit } from '@/constants/Currency';
import { Currency } from '@/constants/Currency';
import { FxConversion } from '@/constants/FxConversion';
import { AnalyticsTrackTypeConstants } from '@/types/AnalyticsTrackTypeConstants';
import type { IPortfolioTree, IPortfolioTreeDraftParams, IPortfolioTreeSubportfolio } from '@/types/IPortfolioTree';
import { isPortfolioTreeSubportfolio } from '@/types/IPortfolioTree';
import type { ComputedRef, Ref, WritableComputedRef } from 'vue';
import { computed, ref } from 'vue';
import { set } from 'vue-demi';
import { useFxRates } from './queries/useDiscoverData';
import { useDiscoverStore } from './useDiscoverStore';
import useTranslation from './useTranslation';
import useStrategyMask from './useStrategyMask';
import { preciseValue } from '@/filters';
import { createGlobalState } from '@vueuse/core';
import { RouteName } from '@/constants/RouteName';
import { PortfolioControllerEvents } from '@/types/Injectables';
import { useCalculateDraft } from './queries/usePortfolioTreeDraft';
import { useItemUnderAnalysisGlobalState } from '@/composables/useItemUnderAnalysis';
import useGlobalEventBus from './useGlobalEventBus';
import useRouteChecks from './useRouteChecks';
import { useRoute } from 'vue-router';
import useStrategyReportingParameters from './useStrategyReportingParameters';
import { useToasts } from './useToasts';

/**
 * This global state is used to track the FX conversion settings that should be applied platform-wide.
 */
const useFxConversionState = createGlobalState(
  (): {
    toCurrency: Ref<Currency>;
    fxType: Ref<FxConversion>;
    originalCurrency: Ref<Currency>;
  } => {
    /**
     * TODO: set these to undefined at first and when item under analysis is changed
     * so that we can prevent unnecessary network calls when these are not set yet
     */
    const toCurrency = ref<Currency>(Currency.USD);
    const fxType = ref<FxConversion>(FxConversion.NOT_CONVERTED);

    /**
     * Stores the original currency of the portfolio when it was first loaded
     *
     * This reference maintains the initial currency of each portfolio and is updated
     * whenever the user navigates to a different portfolio. It's primarily used to
     * determine when currency conversion warnings should be displayed, especially for
     * historical data visualization where metrics remain in their original currency.
     *
     * @remarks
     * - Updated by the Navigation Panel component when portfolio drafts change
     * - Used to compare against the current display currency (toCurrency)
     * - Critical for historical VaR displays which don't convert with the UI
     */
    const originalCurrency = ref<Currency>(Currency.USD);

    return {
      toCurrency,
      fxType,
      originalCurrency,
    };
  },
);

/**
 * Changes the fxType or toCurrency for every child of selected component only if isInherited is set to true.
 * Also apply fx rates to the portfolioValue(notional) for currency conversion
 *
 * @param portfolioTree Subportfolio to go through
 * @param params Portfolio Tree fx conversion item to change and its value
 */
const changeFxConversionParam = (
  portfolioTree: IPortfolioTreeSubportfolio,
  { toCurrency, fxType, fxRate }: { toCurrency?: Currency; fxType?: FxConversion; fxRate?: number },
): void => {
  for (const component of portfolioTree.components) {
    // Convert portfolioValue with passed fxRate
    if (isPortfolioTreeSubportfolio(component)) {
      if (fxRate !== 1 && component.portfolioValue) {
        const updatedNotional = preciseValue(component.portfolioValue * (fxRate ?? 1));
        component.portfolioValue = updatedNotional;
      }
    }

    if (fxType !== component.fxType && component.isInherited) {
      component.fxType = fxType;
    }

    // We should not change the toCurrency of pointer portfolios
    if (isPortfolioTreeSubportfolio(component) && component.pointerSlug) continue;

    if (toCurrency !== component.toCurrency) {
      component.toCurrency = toCurrency;
    }

    if (isPortfolioTreeSubportfolio(component)) {
      changeFxConversionParam(component, { fxType, toCurrency, fxRate });
    }
  }
};

/**
 * This function is designed to return an index's name, plus minimal FX conversion information
 */
export function useIndexFxConversionName(
  comparisonBenchmark: ComputedRef<{
    codeOrSlug: string | null | undefined;
    type: AnalyticsTrackTypeConstants | null;
  }>,
): ComputedRef<string> {
  const { allPortfoliosBySlug, allTracksByCode } = useDiscoverStore();
  const { translate } = useTranslation();
  const { getStrategyMask } = useStrategyMask();
  const { fxType, toCurrency } = useFxConversionState();

  return computed(() => {
    if (comparisonBenchmark.value.codeOrSlug == null || comparisonBenchmark.value.type == null) return '';

    const codeOrSlug = comparisonBenchmark.value.codeOrSlug;
    const benchmarkToUse =
      comparisonBenchmark.value.type === AnalyticsTrackTypeConstants.PORTFOLIO
        ? allPortfoliosBySlug.value.get(codeOrSlug)
        : allTracksByCode.value.get(codeOrSlug);

    const benchmarkName = benchmarkToUse
      ? getStrategyMask(benchmarkToUse)
      : translate({
          path: 'FACTSHEETS.PERFORMANCE_SUMMARY.BENCHMARK_UNAVAILABLE',
        });

    if (fxType.value === FxConversion.NOT_CONVERTED) return benchmarkName;

    // If the benchmark name includes the currency, don't show the FX Conversion
    if (benchmarkName.includes(toCurrency.value)) return benchmarkName;

    if (fxType.value === FxConversion.MONTHLY_HEDGED) {
      // For a strategy benchmark, if its original currency is the same the applied currency, then only show the currency
      if (comparisonBenchmark.value.type === AnalyticsTrackTypeConstants.TRACK) {
        const currency = allTracksByCode.value.get(codeOrSlug)?.currency ?? '';
        if (currency === toCurrency.value) return `${benchmarkName} (${toCurrency.value})`;
      }

      // Otherwise, show the fx conversion applied
      return `${benchmarkName} (${toCurrency.value} ${translate({
        path: 'GLOBAL.FX_CONVERSION.MONTHLY_HEDGED_SHORT',
      })})`;
    }

    // Just show the applied currency for Unhedged
    return `${benchmarkName} (${toCurrency.value})`;
  });
}

/**
 * Composable for handling FX conversion logic
 */
export function useFxConversionHandler(): {
  handleFxChanges: ({
    newFxType,
    newToCurrency,
  }: {
    newFxType?: FxConversion;
    newToCurrency: Currency;
  }) => Promise<void>;
  applyDraftMasterPortfolioTreeFxConversion: ({
    newFxType,
    newToCurrency,
    fxRate,
  }: {
    newFxType: FxConversion;
    newToCurrency: Currency;
    fxRate: number;
  }) =>
    | Promise<{
        newDefaultModeDraft: IPortfolioTree | null;
        newViewModeDraft: IPortfolioTree | null;
        paramsUsed: Partial<IPortfolioTreeDraftParams>;
      }>
    | undefined;
} {
  const { toCurrency, fxType } = useFxConversion();
  const { translate } = useTranslation();
  const route = useRoute();
  const { isOnConstituentRisk, isOnPosition } = useRouteChecks(route);
  const {
    masterPortfolioTreeTempRef,
    shouldTreatRootItemAsPortfolio,
    shouldTreatRootItemAsPortfolioOnConstituentTools,
  } = useItemUnderAnalysisGlobalState();
  const { reportingNotional, updateParameters: updateStrategyReportingParameters } = useStrategyReportingParameters();
  const calculateMutation = useCalculateDraft();
  const { data: fxRates } = useFxRates();
  const { errorToast } = useToasts();
  const { eventBus } = useGlobalEventBus();

  /**
   * Updates FX params on the draft master portfolio tree, ensuring calculations are triggered.
   */
  const applyDraftMasterPortfolioTreeFxConversion = ({
    newFxType,
    newToCurrency,
    fxRate,
  }: {
    newFxType: FxConversion;
    newToCurrency: Currency;
    fxRate: number;
  }):
    | Promise<{
        newDefaultModeDraft: IPortfolioTree | null;
        newViewModeDraft: IPortfolioTree | null;
        paramsUsed: Partial<IPortfolioTreeDraftParams>;
      }>
    | undefined => {
    if (!masterPortfolioTreeTempRef.value) {
      return;
    }

    const portfolioTree = masterPortfolioTreeTempRef.value.portfolioTree;

    // Update FX type and target currency if needed
    if (newFxType !== portfolioTree.fxType) {
      set(portfolioTree, 'fxType', newFxType);
    }

    if (newToCurrency !== portfolioTree.toCurrency) {
      set(portfolioTree, 'toCurrency', newToCurrency);
    }

    // Update portfolio value based on FX rate
    if (portfolioTree.portfolioValue && fxRate) {
      const updatedNotional = preciseValue(portfolioTree.portfolioValue * fxRate);
      set(portfolioTree, 'portfolioValue', updatedNotional);
    }

    // Change FX parameters in the portfolio tree and also apply fx rates to the portfolioValue(notional) for currency conversion
    changeFxConversionParam(masterPortfolioTreeTempRef.value.portfolioTree, {
      fxType: newFxType,
      toCurrency: newToCurrency,
      fxRate,
    });

    const excludedPortfolioTreeSavingPromptRoutes = [
      RouteName.PRINCIPAL_COMPONENT_ANALYSIS,
      RouteName.FACTSHEET,
      RouteName.AI_REPORT,
    ];

    // The portfolio tree cannot be saved in PCA yet, so we do not show the prompts asking us to save
    // And, we do not save the portfolio tree from Portfolio Factsheet
    if (!excludedPortfolioTreeSavingPromptRoutes.includes(route.name as RouteName))
      // Emit change event to trigger save prompt
      eventBus.emit(PortfolioControllerEvents.SET_PORTFOLIO_TREE_HAS_CHANGES);

    // Trigger async mutation for recalculations
    return calculateMutation.mutateAsync({
      masterPortfolioTreeTempRef: masterPortfolioTreeTempRef.value,
      shouldRetriggerDefaultModeCalculations: true,
      shouldRetriggerViewModeCalculations: true,
    });
  };

  /**
   * Handle FX changes on both portfolios and strategies
   *
   * 1. Calculate FX rates using the current and target currencies.
   * 2. Manage FX conversions for the master portfolio tree when on the portfolio page.
   * 3. Update reporting notional values for strategy-related pages.
   * 4. Update global FX parameters after completing all calculations and mutations.
   *
   * Updates to the master portfolio tree are prioritized on the portfolio page because the actual FX conversion
   * on a portfolio page via the API depends on the master portfolio tree's FX parameters.
   */
  const handleFxChanges = async ({
    newFxType = fxType.value,
    newToCurrency,
  }: {
    /**
     * If fxType is not provided (currency-only change on Risk and Position pages), we use the existing fxType
     */
    newFxType?: FxConversion;
    newToCurrency: Currency;
  }): Promise<void> => {
    if (newFxType === fxType.value && newToCurrency === toCurrency.value) return;

    // At this point, the global toCurrency hasn't changed yet
    const oldToCurrency = toCurrency.value;

    // Calculate FX rate
    const fxRate = calculateFxRate({
      fromCurrency: oldToCurrency,
      toCurrency: newToCurrency,
      fxRatesMap: fxRates.value,
    });

    if (fxRate === null) {
      errorToast(translate({ path: 'GLOBAL.ERROR.FX_CURRENCY_NOT_AVAILABLE' }));
      return;
    }

    if (shouldTreatRootItemAsPortfolio.value) {
      const portfolioTree = masterPortfolioTreeTempRef?.value?.portfolioTree;

      // Update master portfolio tree if FX type or currency has changed
      if (portfolioTree && (newFxType !== portfolioTree.fxType || newToCurrency !== portfolioTree.toCurrency)) {
        try {
          await applyDraftMasterPortfolioTreeFxConversion({ newFxType, newToCurrency, fxRate });
        } catch (error) {
          console.error('Failed to update FX conversion on master portfolio tree:', error);
          return;
        }
      }
    }

    // Update reporting notional for strategy-related pages
    if (
      (isOnConstituentRisk.value || isOnPosition.value) &&
      shouldTreatRootItemAsPortfolioOnConstituentTools.value === false
    ) {
      const updatedReportingNotional = preciseValue(reportingNotional.value * fxRate);
      updateStrategyReportingParameters(updatedReportingNotional.toString());
    }

    // Update global FX state at last to maintain synchronization with the draft master portfolio tree
    fxType.value = newFxType;
    toCurrency.value = newToCurrency;
  };

  return {
    handleFxChanges,
    applyDraftMasterPortfolioTreeFxConversion,
  };
}

/**
 * Calculates the FX rate between two currencies using the provided FX rates map.
 */
export function calculateFxRate({
  fromCurrency,
  toCurrency,
  fxRatesMap,
}: {
  fromCurrency: Currency | CurrencyWithSubunit;
  toCurrency: Currency;
  fxRatesMap: { [key: string]: number } | undefined;
}): number | null {
  // No conversion needed if currencies are the same or fxRatesMap is not defined
  if (!fxRatesMap || fromCurrency === toCurrency) return 1;

  // Get fx rates (other currency to USD rate), defaulting to 1 for USD
  const fromCurrencyRate = fromCurrency === Currency.USD ? 1 : fxRatesMap[fromCurrency];
  const toCurrencyRate = toCurrency === Currency.USD ? 1 : fxRatesMap[toCurrency];

  // If either rate is invalid or 0, return null
  if (!fromCurrencyRate || !toCurrencyRate) {
    return null;
  }

  // if fromCurrency or toCurrency is not USD and the fx rate is 1, return '-'
  // this means that the fx rate is not available and we are defaulting to 1
  if (fromCurrency !== Currency.USD && fromCurrencyRate === 1) return null;

  if (toCurrency !== Currency.USD && toCurrencyRate === 1) return null;

  return fromCurrencyRate / toCurrencyRate;
}

export default function useFxConversion({
  localFxType,
  localToCurrency,
}: {
  localFxType?: Ref<FxConversion | null>;
  localToCurrency?: Ref<Currency | null>;
} = {}): {
  toCurrency: WritableComputedRef<Currency>;
  fxType: WritableComputedRef<FxConversion>;
  originalCurrency: ComputedRef<Currency>;
  setOriginalCurrency: (newVal: Currency) => void;
  changeFxConversionParam: (
    portfolioTree: IPortfolioTreeSubportfolio,
    { fxType, toCurrency, fxRate }: { fxType?: FxConversion; toCurrency?: Currency; fxRate?: number },
  ) => void;
} {
  const { toCurrency: globalToCurrency, fxType: globalFxType, originalCurrency } = useFxConversionState();

  const setOriginalCurrency = (newVal: Currency): void => {
    originalCurrency.value = newVal;
  };

  const toCurrency = computed({
    get: () => localToCurrency?.value ?? globalToCurrency.value,
    set: (newVal: Currency) => {
      if (localToCurrency) {
        localToCurrency.value = newVal;
      } else {
        globalToCurrency.value = newVal;
      }
    },
  });

  const fxType = computed({
    get: () => localFxType?.value ?? globalFxType.value,
    set: (newVal: FxConversion) => {
      if (localFxType) {
        localFxType.value = newVal;
      } else {
        globalFxType.value = newVal;
      }
    },
  });

  return {
    toCurrency,
    fxType,
    changeFxConversionParam,
    originalCurrency: computed(() => originalCurrency.value),
    setOriginalCurrency,
  };
}
